import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

const ROLE_CHECK_TIMEOUT_MS = 4000;
const ROLE_CHECK_MAX_ATTEMPTS = 2;
const ROLE_CHECK_RETRY_DELAY_MS = 300;
const ROLE_CHECK_RETRY_LOOP_DELAY_MS = 1000;
const AUTH_BOOT_TIMEOUT_MS = 6000;

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRoleTimeoutError(err: unknown) {
  if (!err) return false;
  const message = err instanceof Error ? err.message.toLowerCase() : String(err).toLowerCase();
  return message.includes("timed out") || message.includes("abort");
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(false);

  const initialLoadDone = useRef(false);
  const authEventReceived = useRef(false);
  const latestCheckId = useRef(0);
  const roleResolvedForUserId = useRef<string | null>(null);
  const roleRetryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function clearRoleRetryTimer() {
    if (!roleRetryTimer.current) return;
    clearTimeout(roleRetryTimer.current);
    roleRetryTimer.current = null;
  }

  async function fetchUserRoleWithTimeout(userId: string) {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= ROLE_CHECK_MAX_ATTEMPTS; attempt++) {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      try {
        const rolePromise = supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId)
          .maybeSingle();

        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error("Role check timed out.")), ROLE_CHECK_TIMEOUT_MS);
        });

        const result = await Promise.race([rolePromise, timeoutPromise]);
        const { data, error } = result as Awaited<typeof rolePromise>;

        console.log("[useAuth] role query result for", userId, "→", JSON.stringify(data), "error:", error?.message ?? "none");

        if (error) {
          throw error;
        }

        return data;
      } catch (err) {
        const normalizedError = err instanceof Error ? err : new Error(String(err));

        lastError = normalizedError;
        const shouldRetry = isRoleTimeoutError(normalizedError) && attempt < ROLE_CHECK_MAX_ATTEMPTS;

        if (shouldRetry) {
          console.warn(`[useAuth] role query attempt ${attempt} timed out, retrying...`);
          await wait(ROLE_CHECK_RETRY_DELAY_MS * attempt);
          continue;
        }

        throw normalizedError;
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    }

    throw lastError ?? new Error("Role check failed.");
  }

  async function checkRoles(u: User | null, isInitial: boolean) {
    const checkId = ++latestCheckId.current;

    try {
      if (!u) {
        if (checkId !== latestCheckId.current) return;
        clearRoleRetryTimer();
        setUser(null);
        setIsAdmin(false);
        setIsStaff(false);
        setRoleLoading(false);
        roleResolvedForUserId.current = null;
        return;
      }

      setUser(u);
      setRoleLoading(true);
      const data = await fetchUserRoleWithTimeout(u.id);

      if (checkId !== latestCheckId.current) return;
      clearRoleRetryTimer();
      setIsAdmin(data?.role === "admin");
      setIsStaff(data?.role === "staff");
      roleResolvedForUserId.current = u.id;
      setRoleLoading(false);
    } catch (err) {
      if (checkId !== latestCheckId.current) return;
      console.error("[useAuth] checkRoles failed:", err);

      const canPreserveExistingRole = isRoleTimeoutError(err) && roleResolvedForUserId.current === u?.id;
      if (canPreserveExistingRole) {
        setRoleLoading(false);
        return;
      }

      if (isRoleTimeoutError(err) && u) {
        setRoleLoading(true);
        clearRoleRetryTimer();
        roleRetryTimer.current = setTimeout(() => {
          void checkRoles(u, false);
        }, ROLE_CHECK_RETRY_LOOP_DELAY_MS);
        return;
      }

      clearRoleRetryTimer();
      setIsAdmin(false);
      setIsStaff(false);
      setRoleLoading(false);
    } finally {
      if (isInitial && checkId === latestCheckId.current) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const authBootTimeout = setTimeout(() => {
      if (!isMounted || initialLoadDone.current) return;
      console.error("[useAuth] auth bootstrap timed out");
      initialLoadDone.current = true;
      setLoading(false);
    }, AUTH_BOOT_TIMEOUT_MS);

    // IMPORTANT: subscribe first, then check session.
    // The Supabase client auto-detects OAuth tokens in the URL hash (implicit flow)
    // and fires SIGNED_IN via onAuthStateChange. We must be subscribed before that happens.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!isMounted) return;
      authEventReceived.current = true;
      console.log("[useAuth] onAuthStateChange:", _event, session?.user?.email ?? "no user");

      const isInitial = !initialLoadDone.current;
      await checkRoles(session?.user ?? null, isInitial);

      if (isInitial) {
        initialLoadDone.current = true;
      }
    });

    // Fallback: if onAuthStateChange doesn't fire (e.g. existing session, no redirect),
    // check for an existing session after a short delay.
    const fallbackTimer = setTimeout(async () => {
      if (!isMounted || initialLoadDone.current || authEventReceived.current) return;
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!isMounted || initialLoadDone.current) return;
        console.log("[useAuth] fallback getSession:", session?.user?.email ?? "no session");

        await checkRoles(session?.user ?? null, true);
        initialLoadDone.current = true;
      } catch {
        if (isMounted && !initialLoadDone.current) {
          initialLoadDone.current = true;
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(authBootTimeout);
      clearTimeout(fallbackTimer);
      clearRoleRetryTimer();
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signOut = async () => {
    // Immediately clear state for instant UI feedback
    setUser(null);
    setIsAdmin(false);
    setIsStaff(false);
    setRoleLoading(false);
    roleResolvedForUserId.current = null;
    clearRoleRetryTimer();
    await supabase.auth.signOut();
  };

  return { user, isAdmin, isStaff, loading, roleLoading, signIn, signOut };
}


